import type { PayloadFormatter } from '../shared/types';
import { normalizeHttpHeaders } from '@terra/shared-utils';

/**
 * Formats HTTP executor/guardrail-checker tool args where url/method/headers/body
 * are at the root level (not nested under options).
 */
export const formatHttpExecutorPayload: PayloadFormatter = (args) => {
  const lines: string[] = [];
  const method = (args.method as string) || 'GET';

  if (args.url) {
    lines.push(`**${method.toUpperCase()}** \`${args.url}\``);
  }

  if (args.credentialId) {
    lines.push(`**Credential:** \`${args.credentialId}\``);
  }

  const headers = args.headers as Record<string, unknown> | undefined;
  if (headers && Object.keys(headers).length > 0) {
    const normalizedHeaders = normalizeHttpHeaders(
      Object.fromEntries(
        Object.entries(headers).map(([key, value]) => [
          key,
          Array.isArray(value)
            ? value.map(String).join(', ')
            : String(value ?? ''),
        ]),
      ),
    );
    lines.push('\n**Headers:**');
    Object.entries(normalizedHeaders).forEach(([key, value]) => {
      const displayValue =
        typeof value === 'string' && value.length > 50
          ? value.substring(0, 47) + '...'
          : Array.isArray(value)
            ? value.join(', ')
            : value;
      lines.push(`- \`${key}\`: \`${displayValue}\``);
    });
  }

  const body = args.body as string | undefined;
  if (body) {
    lines.push('\n**Body:**');
    try {
      const parsed = typeof body === 'string' ? JSON.parse(body) : body;
      lines.push(`\`\`\`json\n${JSON.stringify(parsed, null, 2)}\n\`\`\``);
    } catch {
      lines.push(`\`\`\`\n${body}\n\`\`\``);
    }
  }

  const formData = args.formData as string | undefined;
  if (formData) {
    lines.push('\n**Form Data:**');
    lines.push(`\`${formData}\``);
  }

  return lines.length > 0 ? lines.join('\n') : null;
};

/**
 * Formats http-payload-creator input - shows the user's natural language query.
 */
export const formatHttpPayloadCreatorPayload: PayloadFormatter = (args) => {
  const lines: string[] = [];

  if (args.query) {
    lines.push(`**Query:** ${args.query}`);
  }

  if (args.credentialId) {
    lines.push(`**Credential:** \`${args.credentialId}\``);
  }

  return lines.length > 0 ? lines.join('\n') : null;
};
